import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { FormSelect } from "../../../../components/form-components/form-select.components";
import { useEffect } from "react";
import { findErrorMessage, handleHttpError } from "../../../../utils/utils";
import { useAddSocialAdminHttp } from "../../../../hooks/http/admin/socials/add-social.http";
import Spinner from "../../../../components/spinner.components";
import { SocialPlatforms } from "../../../../types/models/social.models.types";

const FormSchema = z.object({
	platform: z.nativeEnum(SocialPlatforms, {
		required_error: "Please select a platform",
	}),
	handle_or_link: z.string().min(1, "Please provide a handle or link"),
	fixed_payout_amount: z
		.string()
		.transform((val) => Number.parseFloat(val))
		.optional(),
});

const platformOptions = [
	{ label: "Instagram", value: SocialPlatforms.instagram },
	{ label: "YouTube", value: SocialPlatforms.youtube },
];

export const AddSocialModalPartial: React.FunctionComponent = () => {
	const navigate = useNavigate();
	const { execute: addSocialHttp, fetching: addingSocial } =
		useAddSocialAdminHttp();

	const { toast } = useToast();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			handle_or_link: "",
		},
	});

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		if (data.handle_or_link) {
			const response = await addSocialHttp({
				body: {
					platform: data.platform,
					handle: data.handle_or_link,
				},
			});
			if (!response.success) {
				return handleHttpError(response, toast);
			}
			toast({
				content: "User added successfully",
				toastType: "success",
			});
			form.reset();
			return navigate(-1);
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.formState.errors) {
			const key = Object.keys(form.formState.errors)[0];

			if (key) {
				const error =
					form.formState.errors[key as keyof typeof form.formState.errors];
				const message = findErrorMessage(error) || "An error occurred";

				toast({
					content: message,
					toastType: "error",
				});
			}
		}
	}, [form.formState.errors]);

	return (
		<DialogContent className="flex flex-col items-center gap-5 border-border w-full max-w-xl text-center text-primary">
			<DialogHeader className="w-full">
				<div className="flex flex-col">
					<p className="flex gap-1 font-semibold text-28 leading-8">
						Add a new social
					</p>
				</div>
			</DialogHeader>
			<div className="border-border border-b w-full" />
			<div className="flex items-center gap-4 w-full">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="relative flex flex-col space-y-8 w-full"
					>
						<FormSelect
							form={form}
							name="platform"
							label="Select platform"
							placeholder="Choose a platform"
							options={platformOptions}
							required
						/>
						<FormInput
							form={form}
							name="handle_or_link"
							label="Enter handle or link"
							placeholder="User name or link here...."
							required
							type="text"
						/>
						<Button
							type="submit"
							className="bg-primarybuttonbg w-40 h-14 font-semibold text-primarybuttontext tracking-wider self-end"
						>
							{addingSocial ? (
								<Spinner width={15} />
							) : (
								"Add social →"
							)}
						</Button>
					</form>
				</Form>
			</div>
		</DialogContent>
	);
};
